import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { agencyApi } from "./agencyApi";
import { formatMoney } from "@/lib/money";
import { STATUS_META, STATUS_STEPS } from "./quoteRequestStatus";
import { WorkThumb, WorkViewer } from "./WorkViewer";
import { computeGroupDescriptions, effectiveLineDescription } from "./lineDescriptions";
import "./agency.css";

// PM Feedback Batch 1, item 1 (2026-09) — lands the agency on the request they just submitted
// (and reused for any request in "My requests"), with a "What happens next" section instead of
// a generic confirmation screen.

// Part 1B, fix (e) (2026-10) — groups the submitted line_items under their package heading
// (section_heading, same field AgencyEstimator.jsx's own EstimateStep groups by) so the agency
// sees the quote itemized by name/description/qty/unit/rate, not just one grand_total figure.
function groupBySectionHeading(lineItems) {
  const order = [];
  const groups = {};
  for (const li of lineItems) {
    const heading = li.section_heading || li.line_label || li.display_name || "Other";
    if (!groups[heading]) { groups[heading] = []; order.push(heading); }
    groups[heading].push(li);
  }
  return order.map((h) => [h, groups[h]]);
}

const _heading = (li) => li.section_heading || li.line_label || li.display_name || "Other";

export default function QuoteRequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    agencyApi.myQuoteRequest(id).then((r) => setItem(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="ag-card" data-testid="agency-quote-request-not-found">
        <p className="ag-h3" style={{ marginBottom: 8 }}>We couldn't find this request</p>
        <button className="ag-link" onClick={() => nav("/agency/quote-requests")} data-testid="agency-quote-request-not-found-back">Back to your requests</button>
      </div>
    );
  }
  if (!item) return <div className="ag-small" data-testid="agency-quote-request-detail-loading">Loading…</div>;

  const meta = STATUS_META[item.status] || STATUS_META["Submitted"];
  const stepIdx = STATUS_STEPS.indexOf(item.status);
  const groupDesc = computeGroupDescriptions(item.line_items || [], _heading, (l) => l.description);

  return (
    <div style={{ maxWidth: 640 }} data-testid="agency-quote-request-detail">
      <button className="ag-link" onClick={() => nav("/agency/quote-requests")} data-testid="agency-quote-request-detail-back">&larr; Your requests</button>
      <h1 className="ag-h1" style={{ fontSize: 30, marginTop: 14 }}>{item.working_title || item.production_type}</h1>
      <span className={`ag-pill ${meta.pillClass}`} style={{ marginTop: 10, display: "inline-block" }} data-testid="agency-quote-request-detail-status">{meta.label}</span>

      {stepIdx >= 0 && (
        <div style={{ display: "flex", gap: 6, margin: "18px 0" }} data-testid="agency-quote-request-timeline">
          {STATUS_STEPS.map((s, i) => (
            <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= stepIdx ? "var(--gold)" : "var(--line)" }} />
          ))}
        </div>
      )}

      <div className="ag-card" style={{ marginTop: 20 }}>
        <div className="ag-grid ag-grid-2" style={{ gap: 16 }}>
          <div><div className="ag-label">Production Type</div><div className="ag-body">{item.production_type}</div></div>
          {item.shoot_location && <div><div className="ag-label">Location</div><div className="ag-body">{item.shoot_location}</div></div>}
          {item.shoot_days && <div><div className="ag-label">Shoot Days</div><div className="ag-body">{item.shoot_days} days</div></div>}
          <div><div className="ag-label">{item.grand_total != null ? "Estimated Production Cost" : "Price"}</div><div className="ag-body" data-testid="agency-quote-request-detail-total">{item.grand_total != null ? formatMoney(item.grand_total, item.currency) : "NJM will price this from your brief"}</div></div>
        </div>
        {item.brief && <p className="ag-small" style={{ marginTop: 14, fontStyle: "italic" }}>"{item.brief}"</p>}
      </div>

      {item.line_items?.length > 0 && (
        <div className="ag-card" style={{ marginTop: 16 }} data-testid="agency-quote-request-line-items">
          <p className="ag-eyebrow" style={{ marginBottom: 10 }}>Itemized estimate</p>
          {groupBySectionHeading(item.line_items).map(([heading, items], gi) => (
            <div key={heading} style={{ marginTop: gi > 0 ? 14 : 0 }}>
              <p className="ag-label" style={{ marginBottom: 6 }}>{heading}</p>
              {items.map((li, i) => {
                const desc = effectiveLineDescription(li, _heading, (l) => l.description, groupDesc);
                return (
                  <div key={i} className="ag-grid ag-grid-2" style={{ gap: 8, padding: "6px 0", borderTop: i > 0 ? "1px solid var(--line)" : "none" }} data-testid={`agency-quote-request-line-item-${gi}-${i}`}>
                    <div>
                      <div className="ag-body">{li.line_label || li.display_name}</div>
                      {desc && <div className="ag-small" style={{ color: "var(--ink-2)" }} data-testid={`agency-quote-request-line-item-desc-${gi}-${i}`}>{desc}</div>}
                      <div className="ag-small">{li.qty}{li.unit ? ` × ${li.unit}` : ""}</div>
                    </div>
                    <div className="ag-body" style={{ textAlign: "right" }}>
                      {li.amount != null ? formatMoney(li.amount, item.currency) : "NJM to quote"}
                      {li.amount_breakdown && <div className="ag-small" style={{ color: "var(--ink-2)" }}>{li.amount_breakdown}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <div className="ag-card" style={{ marginTop: 16, background: "var(--paper-2)", border: "none" }}>
        <p className="ag-eyebrow" style={{ marginBottom: 10 }}>What happens next</p>
        <p className="ag-body" data-testid="agency-quote-request-whats-next">{meta.next}</p>
      </div>

      {item.staff_note && (
        <div className="ag-card" style={{ marginTop: 16 }} data-testid="agency-quote-request-staff-note">
          <p className="ag-label" style={{ marginBottom: 6 }}>Note from NJM</p>
          <p className="ag-body">{item.staff_note}</p>
        </div>
      )}

      {item.reference_videos?.length > 0 && (
        <div className="ag-card" style={{ marginTop: 16 }} data-testid="agency-quote-request-reference-videos">
          <p className="ag-label" style={{ marginBottom: 10 }}>Reference videos you attached</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
            {item.reference_videos.map((v) => (
              <WorkThumb key={v.id} sample={{ ...v, role_tag: v.category }} onOpen={setViewing} label={v.title} />
            ))}
          </div>
        </div>
      )}

      {viewing && <WorkViewer sample={viewing} onClose={() => setViewing(null)} />}

      <p className="ag-small" style={{ marginTop: 18 }}>Production only — editing, scripting, storyboarding and other creative/post-production services are quoted separately.</p>
    </div>
  );
}
